export type User = {
    id?: string;
    first_name: string;
    last_name: string;
    email: string;
    country: string;
    role?: string;
}

// Type used to simplify the device object
export type Device = {
    id: number;
    type: DeviceType;
    name?: string;
    status?: string;
    properties?: Properties;
    availableModes?: FlashMode[];
    defaultModes?: {id: number, position: number, setup: any}[];
    profiles: Profile[];
    owner?: string;
    hardwareId?: string;
    firmware?: number;
}

// Actual device object returned from the API
export type DeviceRecord = {
    owner?: string;
    type?: number;
    name?: string;
    hardware_id?: string;
    properties?: Properties;
    profiles?: Profile[];
    status?: string;
    firmware?: number;
}

export type FlashMode = {
    id: number;
    name: string;
    mode_type: string;
    flash_pattern: FlashCommand[];
    eye_saver_pattern: FlashCommand[];
    settings: Settings;
    config: {[key: string]: any};
    allow_custom: boolean;
    tilt?: boolean;
    eye_saver?: boolean;
    brake_patterns: number[];
    // Actual pattern data, not from mode record
    brake_patterns_data?: {name: string, pattern: FlashCommand[], }[];
    default_pattern?: boolean;
    order?: number;
    default?: boolean;
}

export type FlashPattern = {
    duration: number; 
    channels: {
        channel: number;
        brightness: number;
    }[];
    index: number;
}

export type CobberPattern = {
    setup?: {
        x?: {
            "shortFactor": number,
            "longFactor": number,
            "threshold": number,
        },
        z?: {
            "shortFactor": number,
            "longFactor": number,
            "threshold": number,
        }
    },
    x_patterns?: FlashCommand[][],
    z_patterns?: FlashCommand[][]
}

export type ProfileMode = {
    id: number;
    config: {[key: string]: any};
}

export type DeviceType = {
    id: number;
    status: string;
    name: string;
    code: string;
    product_id: number;
    chip_id: number;
    power: {
        min: number,
        max: {
            "6v": [{power: number, value: number}],
            "12v": [{power: number, value: number}]
        }
    };
    custom_memory: {[key: string]: number},
    eye_saver_compatible?: boolean;
    braking_compatible?: boolean;
    sensitivities: {
        accel_range: number[],
        sleep_range: number[],
        default: number
    }
    default_sleep: number;
    default_wake: number;
    default_smart_lighting: boolean;
}
    
export type Properties = {
    [key: string]: string | number | boolean;
}

export type Settings = {
    [key: string]: any; 
}

export type Profile = {
    id: number;
    name: string;
    settings: Settings;
    steady_modes: ProfileMode[];
    flash_modes: ProfileMode[];
}

export type AuthTokens = {
    access: string;
    refresh: string;
    expires: string;
}

export type FaqRow = {
    question: string,
    answer: string
}

export type FaqCategory = {
    title: string,
    rows: FaqRow[]
}

export type Firmware = {
    eraseblocks: {start: number, end: number},
    memorypayload: Block[]
}

export type FirmwareRecord = {
    id: number;
    name: string;
    device_type: number;
    version_number: number;
    crc: number;
    file: string;
}

export type Block = {
    block: boolean,
    start_address: number,
    end_address: number,
    length: number,
    data: any
}

export type FlashCommand = {
    command: string,
    data: number[] | number
}

export const defaultProfile: Profile = {
    id: 1,
    name: 'Default',
    "settings": {},
    steady_modes: [],
    flash_modes: []
}

export const emptyMode: FlashMode = {
    id: 0,
    name: '',
    mode_type: '',
    flash_pattern: [],
    eye_saver_pattern: [],
    allow_custom: false,
    config: {},
    settings: {},
    brake_patterns: []
}

export type FormOptions = {
    fields: FormField[];
    submit: () => void;
    buttonText: string;
    buttonClass?: string;
    buttonWrapperClass?: string;
    loading?: boolean;
    horizontal?: boolean;
}

export type FormField = {
    label: string;
    type: string;
    placeholder?: string;
    setValue: (value: any) => void;
    value: any;  
    values?: { key: string, value: string }[];
    forgotPassword?: boolean;
    name?: string;
    filterable?: boolean;
    startKey?: string;
    required? : boolean;
}